import { useState } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Loader2, Gift, CheckCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { GiftCardVoucher } from '@/components/GiftCardVoucher';
import { GIFT_BONUS_AMOUNT, GIFT_BONUS_DEADLINE_LABEL, isAperitivoBonusActive } from '@/data/giftcards2027';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GiftCardRequestModal({ card, open, onClose }) {
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [dedication, setDedication] = useState('');
  const [gdpr, setGdpr] = useState(false);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!card) return null;
  const bonusActive = card.amount === 200 && isAperitivoBonusActive();

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setSuccess(false);
      setBuyerName('');
      setBuyerEmail('');
      setRecipientName('');
      setDedication('');
      setGdpr(false);
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!buyerName.trim() || !buyerEmail.trim()) {
      toast.error('Inserisci nome e email');
      return;
    }
    if (!gdpr) {
      toast.error("Accetta l'informativa privacy per proseguire");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/giftcard/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: card.amount,
          buyer_name: buyerName,
          buyer_email: buyerEmail,
          recipient_name: recipientName || null,
          dedication: dedication || null,
          gdpr_consent: gdpr,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.payment_url) {
          window.location.href = data.payment_url;
          return;
        }
        toast.error('Errore nella creazione del pagamento. Riprova.');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Errore nella creazione del pagamento. Riprova.');
      }
    } catch {
      toast.error('Errore di connessione. Riprova tra qualche istante.');
    }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-ivory" data-testid="giftcard-modal">
        {success ? (
          <div className="text-center py-10" data-testid="giftcard-success">
            <CheckCircle size={48} className="text-champagne mx-auto mb-4" />
            <h3 className="font-heading text-2xl text-night mb-3">Richiesta inviata!</h3>
            <p className="font-body text-stone-600 max-w-md mx-auto mb-2">
              Grazie {buyerName.split(' ')[0]}. Ti contatteremo a breve all'indirizzo{' '}
              <strong className="text-night">{buyerEmail}</strong> per completare l'acquisto e consegnarti
              il buono digitale personalizzato.
            </p>
            <p className="font-body text-xs text-stone-400 mb-8">
              La richiesta non comporta alcun pagamento immediato.
            </p>
            <Button
              onClick={handleClose}
              className="bg-night text-ivory hover:bg-stone-800 rounded-full px-8"
              data-testid="giftcard-success-close"
            >
              Chiudi
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <DialogHeader className="mb-4">
              <DialogTitle className="font-heading text-2xl text-night flex items-center gap-2">
                <Gift size={22} className="text-champagne" /> Acquista la Gift Card
              </DialogTitle>
              <DialogDescription className="font-body text-stone-500">
                {card.nome} — pagamento sicuro con Nexi XPay. Dopo la conferma ricevi subito il buono digitale
                con codice univoco via email.
              </DialogDescription>
            </DialogHeader>

            {/* Riepilogo */}
            <div className="bg-white border border-stone-200 rounded-xl p-4 mb-5" data-testid="giftcard-modal-summary">
              <div className="flex items-center justify-between font-body text-sm text-stone-600">
                <span>Gift Card {card.amount} €</span>
                <span className="text-night font-medium">{card.amount} €</span>
              </div>
              {bonusActive && (
                <div className="flex items-center justify-between font-body text-sm text-champagne mt-2" data-testid="giftcard-modal-bonus">
                  <span>Bonus aperitivo (in omaggio, entro il {GIFT_BONUS_DEADLINE_LABEL})</span>
                  <span className="font-medium">+{GIFT_BONUS_AMOUNT} €</span>
                </div>
              )}
              {bonusActive && (
                <p className="font-body text-[11px] text-stone-400 mt-1">
                  Il bonus di {GIFT_BONUS_AMOUNT} € è utilizzabile esclusivamente per l'aperitivo e resta separato dai {card.amount} € di credito.
                </p>
              )}
              <div className="flex items-center justify-between font-heading text-lg text-night border-t border-stone-200 mt-3 pt-3" data-testid="giftcard-modal-total">
                <span>Totale</span>
                <span>{card.amount} €</span>
              </div>
            </div>

            {/* Anteprima buono */}
            <div className="mb-6">
              <GiftCardVoucher
                amount={card.amount}
                bonus={bonusActive ? GIFT_BONUS_AMOUNT : 0}
                dedication={dedication}
                recipient={recipientName}
                sample={!dedication}
              />
            </div>

            {/* Dati */}
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <Label htmlFor="gc-buyer-name" className="text-night">Il tuo nome *</Label>
                <Input
                  id="gc-buyer-name"
                  value={buyerName}
                  onChange={(e) => setBuyerName(e.target.value)}
                  placeholder="Nome e cognome"
                  className="mt-1 bg-white"
                  required
                  data-testid="giftcard-buyer-name"
                />
              </div>
              <div>
                <Label htmlFor="gc-buyer-email" className="text-night">La tua email *</Label>
                <Input
                  id="gc-buyer-email"
                  type="email"
                  value={buyerEmail}
                  onChange={(e) => setBuyerEmail(e.target.value)}
                  placeholder="nome@email.it"
                  className="mt-1 bg-white"
                  required
                  data-testid="giftcard-buyer-email"
                />
              </div>
            </div>
            <div className="mb-4">
              <Label htmlFor="gc-recipient" className="text-night">Nome del destinatario (facoltativo)</Label>
              <Input
                id="gc-recipient"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="A chi è dedicato il regalo?"
                className="mt-1 bg-white"
                data-testid="giftcard-recipient-name"
              />
            </div>
            <div className="mb-5">
              <Label htmlFor="gc-dedication" className="text-night">Dedica (facoltativa)</Label>
              <Textarea
                id="gc-dedication"
                value={dedication}
                onChange={(e) => setDedication(e.target.value.slice(0, 200))}
                placeholder="Scrivi un messaggio: lo vedrai comparire nell'anteprima del buono"
                className="mt-1 bg-white resize-none"
                rows={2}
                data-testid="giftcard-dedication"
              />
              <p className="font-body text-[11px] text-stone-400 mt-1 text-right">{dedication.length}/200</p>
            </div>

            <div className="flex items-start gap-3 mb-6">
              <Checkbox
                id="gc-gdpr"
                checked={gdpr}
                onCheckedChange={(v) => setGdpr(!!v)}
                className="mt-0.5"
                data-testid="giftcard-gdpr-checkbox"
              />
              <label htmlFor="gc-gdpr" className="font-body text-xs text-stone-500 leading-relaxed cursor-pointer">
                Ho letto le <a href="#gift-card-regole" onClick={handleClose} className="underline hover:text-champagne">condizioni di utilizzo</a> e
                l'<Link to="/privacy" target="_blank" className="underline hover:text-champagne">informativa privacy</Link>, e
                acconsento al trattamento dei dati per gestire il mio acquisto. *
              </label>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="submit"
                disabled={sending}
                className="flex-1 bg-champagne text-night hover:bg-night hover:text-ivory rounded-full py-6 text-sm uppercase tracking-widest whitespace-normal h-auto"
                data-testid="giftcard-submit-btn"
              >
                {sending ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" /> Reindirizzamento al pagamento...
                  </>
                ) : (
                  'Procedi al pagamento sicuro'
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="border-stone-300 text-stone-600 rounded-full py-6 text-sm uppercase tracking-widest h-auto"
                data-testid="giftcard-cancel-btn"
              >
                Annulla
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
